import { action, Action, thunk, Thunk } from "easy-peasy";
import { StoreModel } from "~/store";
import { API } from "~/app/routes";
import { CommonModel, getCommonModel } from "~/store/commonStore";
import axios, { CancelTokenSource } from "axios";
import { Notification, NotificationData, NotificationMessage, NotificationPayload, UnreadNotificationCount, UpdateNotificationStatusPayload, UpdateNotificationsData } from "~/types";

export interface NotificationModel {
    notifications: NotificationData['userNotifications'];
    setNotifications: Action<NotificationModel, NotificationData['userNotifications']>;
    totalNotificationsCount: NotificationData['pagination']['totalRecords'];
    setTotalNotificationsCount: Action<NotificationModel, NotificationData['pagination']['totalRecords']>;
    cancelToken: CancelTokenSource | null;
    setCancelToken: Action<NotificationModel, CancelTokenSource | null>;
    fetchNotifications: Thunk<NotificationModel, NotificationPayload, any, StoreModel, Promise<void>>;
    resetNotifications: Action<NotificationModel, void>;

    fetching: boolean
    setFetching: Action<CommonModel, boolean>
    isSearchOpen: boolean;
    setIsSearchOpen: Action<NotificationModel, boolean>;
    isSearching: boolean;
    setIsSearching: Action<NotificationModel, boolean>;

    notificationPayload: NotificationPayload;
    setNotificationPayload: Action<NotificationModel, NotificationPayload>;

    unreadNotificationCount: number;
    addChatNotification: Action<NotificationModel, NotificationMessage>;
    fetchUnreadNotificationsCount: Thunk<NotificationModel, void, any, StoreModel, Promise<void>>;
    setUnreadNotificationCount: Action<NotificationModel, UnreadNotificationCount>;
    updateNotificationsStatus: Thunk<NotificationModel, UpdateNotificationStatusPayload, void, StoreModel, Promise<void>>;
    updatedNotificationsStatus: Action<NotificationModel, UpdateNotificationsData>;
    markAllAsRead: Thunk<NotificationModel, void, void, StoreModel, Promise<void>>;
    markAllAsReadSuccess: Action<NotificationModel, void>;
}

export const notificationModel: NotificationModel = {
    notifications: [],
    setNotifications: action((state, payload) => {
        state.notifications = payload;
    }),
    totalNotificationsCount: 0,
    setTotalNotificationsCount: action((state, payload) => {
        state.totalNotificationsCount = payload;
    }),
    cancelToken: null,
    setCancelToken: action((state, payload) => {
        state.cancelToken = payload;
    }),
    fetchNotifications: thunk((actions, payload, {getStoreState}) => {
        const storeState = getStoreState().notification;
        // To show loader for search and first time load only
        actions.setFetching(!storeState.notifications.length || storeState.isSearching || payload.offset === 0 ? true : false);
        //Check if there are any previous pending requests
        if(storeState.cancelToken != null) {
            storeState.cancelToken.cancel("Operation canceled due to new request.");
        }
        //Save the cancel token for the current request
        actions.setCancelToken(axios.CancelToken.source());
        return axios
            .post<NotificationPayload, NotificationData>(
                `${API.users}${API.notifications}/notification`, payload, { cancelToken: getStoreState().notification.cancelToken?.token }
            )
            .then((data) => {
                const {userNotifications = [], pagination : {totalRecords = 0}} = data;
                actions.setNotifications([
                    ...storeState.isSearching || payload.offset === 0 ? [] : storeState.notifications,
                    ...userNotifications
                ]);
                actions.setTotalNotificationsCount(totalRecords);
            }).catch((err) => {
                if (axios.isCancel(err)) {
                    console.log(err.message);
                }
            })
            .finally(() => {
                actions.setFetching(false);
                actions.setIsSearching(false);
            });
    }),
    resetNotifications: action((state) => {
        const chatTypes = state.notificationPayload.chatTypes ?? [0];
        const searchText = state.notificationPayload.searchText ?? '';
        state.notifications = [];
        state.totalNotificationsCount = 0;
        state.notificationPayload = { offset: 0, searchText, chatTypes };
    }),

    fetching: false,
    setFetching: action((state, payload) => {
      state.fetching = payload;
    }),
    isSearchOpen: false,
    setIsSearchOpen: action((state, payload) => {
        state.isSearchOpen = payload;

        // reload data on search close event
        if (payload === false && state.notificationPayload.searchText && state.notificationPayload.searchText.length > 0) {
            state.notifications = [];
            state.notificationPayload = {
                searchText: "",
                offset: 0,
                chatTypes: state.notificationPayload.chatTypes ?? [0],
            };
        }
    }),
    isSearching: false,
    setIsSearching: action((state, payload) => {
        state.isSearching = payload;
    }),

    notificationPayload: {
        offset: 0,
        searchText: "",
        chatTypes: [0],
    },
    setNotificationPayload: action((state, payload) => {
        state.notificationPayload = payload;
    }),

    unreadNotificationCount: 0,
    addChatNotification: action((state, payload) => {
        // do not update the  unreadNotificationCount when a message is sent by the current logged-in user
        state.unreadNotificationCount = payload.messageStatus === false ? state.unreadNotificationCount+1 : state.unreadNotificationCount;
        // remove data for a particular issue id if already available (one row per issue or conversion)
        if (payload?.issueId) {
            state.notifications = state.notifications.filter(
                (data) =>
                    !(
                        String(data.issueId) === String(payload.issueId) &&
                        Number(data.messageType) === Number(payload.type)
                    )
            );
        }
        // add latest data on top
        state.notifications = [
            {
                id: "",
                ...payload,
                createdAt: payload.dateTime || "",
                updatedAt: payload.dateTime || "",
                messageType: Number(payload.type),
                messageStatus: payload.messageStatus,
                importance: Number(payload.importance) || 1
            },
            ...state.notifications,
        ];
    }),
    fetchUnreadNotificationsCount: thunk((actions) => {
        return axios.get<null, UnreadNotificationCount>(`${API.users}/get_unread_notifications_count/notification`)
            .then((data) => {
                actions.setUnreadNotificationCount(data);
            });
    }),
    setUnreadNotificationCount: action((state, payload) => {
        state.unreadNotificationCount = payload.unreadNotificationCount;
    }),
    updateNotificationsStatus: thunk((actions, payload) => {
        return axios
            .post<UpdateNotificationsData, UpdateNotificationsData>(
                `${API.issue}/update_notification_status/notification`, payload
            )
            .then((data) => {
                actions.updatedNotificationsStatus({...data, messageId: payload.messageId});
            });
    }),
    updatedNotificationsStatus: action((state, payload) => {
        state.notifications.map((element) => {
            if(Number(element.id) === payload.messageId && element.messageStatus !== true) {
                element.messageStatus = true;
            }
            return element;
        })
        state.unreadNotificationCount = Math.max(
            0,
            state.unreadNotificationCount - (payload?.numberOfNotificationsReads || 0)
        );
    }),
    markAllAsRead: thunk((actions) => {
        return axios
            .post(`${API.users}/mark_all_read/notification`)
            .then(() => {
                actions.markAllAsReadSuccess();
            });
    }),
    markAllAsReadSuccess: action((state) => {
        state.notifications = state.notifications.map((element) => ({
            ...element,
            messageStatus: true
        }));
        state.unreadNotificationCount = 0;
    })
};

export interface ChatModel extends CommonModel<Notification> {
    notifications: NotificationData['userNotifications'];
    setNotifications: Action<ChatModel, NotificationData['userNotifications']>;
    totalNotificationsCount: NotificationData['pagination']['totalRecords'];
    setTotalNotificationsCount: Action<ChatModel, NotificationData['pagination']['totalRecords']>;
    cancelToken: CancelTokenSource | null;
    setCancelToken: Action<ChatModel, CancelTokenSource | null>;
    fetchNotifications: Thunk<ChatModel, NotificationPayload, any, StoreModel, Promise<void>>;
    resetNotifications: Action<ChatModel, void>;

    isSearchOpen: boolean;
    setIsSearchOpen: Action<ChatModel, boolean>;
    isSearching: boolean;
    setIsSearching: Action<ChatModel, boolean>;

    notificationPayload: NotificationPayload;
    setNotificationPayload: Action<ChatModel, NotificationPayload>;

    unreadNotificationCount: number;
    perTypeCount: Record<number, number>;
    addChatNotification: Action<ChatModel, NotificationMessage>;
    fetchUnreadNotificationsCount: Thunk<ChatModel, void, any, StoreModel, Promise<void>>;
    setUnreadNotificationCount: Action<ChatModel, UnreadNotificationCount>;
    updateNotificationsStatus: Thunk<ChatModel, UpdateNotificationStatusPayload, void, StoreModel, Promise<void>>;
    updatedNotificationsStatus: Action<ChatModel, UpdateNotificationsData>;
    markAllAsRead: Thunk<ChatModel, { type?: number } | void, void, StoreModel, Promise<void>>;
    markAllAsReadSuccess: Action<ChatModel, { type?: number } | void>;
}

export const chatModel: ChatModel = {
    ...getCommonModel<Notification>(API.notifications),

    notifications: [],
    setNotifications: action((state, payload) => {
        state.notifications = payload;
    }),
    totalNotificationsCount: 0,
    setTotalNotificationsCount: action((state, payload) => {
        state.totalNotificationsCount = payload;
    }),
    cancelToken: null,
    setCancelToken: action((state, payload) => {
        state.cancelToken = payload;
    }),
    fetchNotifications: thunk((actions, payload, {getStoreState}) => {
        const storeState = getStoreState().chat;
        // To show loader for search and first time load only
        actions.setFetching(!storeState.notifications.length || storeState.isSearching || payload.offset === 0 ? true : false);
        //Check if there are any previous pending requests
        if(storeState.cancelToken != null) {
            storeState.cancelToken.cancel("Operation canceled due to new request.");
        }
        //Save the cancel token for the current request
        actions.setCancelToken(axios.CancelToken.source());
        return axios
            .post<NotificationPayload, NotificationData>(
                `${API.users}${API.notifications}/chat`, payload, { cancelToken: getStoreState().chat.cancelToken?.token }
            )
            .then((data) => {
                const {userNotifications = [], pagination : {totalRecords = 0}} = data;
                actions.setNotifications([
                    ...storeState.isSearching || payload.offset === 0 ? [] : storeState.notifications,
                    ...userNotifications
                ]);
                actions.setTotalNotificationsCount(totalRecords);
            }).catch((err) => {
                if (axios.isCancel(err)) {
                    console.log(err.message);
                }
            })
            .finally(() => {
                actions.setFetching(false);
                actions.setIsSearching(false);
            });
    }),
    resetNotifications: action((state) => {
        const chatTypes = state.notificationPayload.chatTypes ?? [];
        state.notifications = [];
        state.totalNotificationsCount = 0;
        state.notificationPayload = { offset: 0, searchText: '', chatTypes };
    }),

    isSearchOpen: false,
    setIsSearchOpen: action((state, payload) => {
        state.isSearchOpen = payload;

        // reload data on search close event
        if (payload === false && state.notificationPayload.searchText && state.notificationPayload.searchText.length > 0) {
            const chatTypes = state.notificationPayload.chatTypes ?? [];
            state.notifications = [];
            state.notificationPayload = { searchText: "", offset: 0, chatTypes };
        }
    }),
    isSearching: false,
    setIsSearching: action((state, payload) => {
        state.isSearching = payload;
    }),

    notificationPayload: {
        offset: 0,
        searchText: ""
    },
    setNotificationPayload: action((state, payload) => {
        state.notificationPayload = payload;
    }),

    unreadNotificationCount: 0,
    perTypeCount: {},
    addChatNotification: action((state, payload) => {
        // do not update the  unreadNotificationCount when a message is sent by the current logged-in user
        state.unreadNotificationCount = payload.messageStatus === false ? state.unreadNotificationCount+1 : state.unreadNotificationCount;
        if (payload.messageStatus === false && payload.type != null) {
            const typeKey = Number(payload.type);
            state.perTypeCount[typeKey] = (state.perTypeCount[typeKey] || 0) + 1;
        }
        // remove data for a particular issue id if already available (one row per issue or conversion)
        if (payload?.issueId) {
            state.notifications = state.notifications.filter((data) => data.issueId !== payload.issueId);
        }
        // add latest data on top
        state.notifications = [
            {
                id: "",
                ...payload,
                createdAt: payload.dateTime || "",
                updatedAt: payload.dateTime || "",
                messageType: Number(payload.type),
                messageStatus: payload.messageStatus,
                importance: Number(payload.importance) || 1
            },
            ...state.notifications,
        ];
    }),
    fetchUnreadNotificationsCount: thunk((actions) => {
        return axios.get<null, UnreadNotificationCount>(`${API.users}/get_unread_notifications_count/chat`)
            .then((data) => {
                actions.setUnreadNotificationCount(data);
            });
    }),
    setUnreadNotificationCount: action((state, payload) => {
        state.unreadNotificationCount = payload.unreadNotificationCount;
        if (payload.perTypeCount) {
            state.perTypeCount = payload.perTypeCount;
        }
    }),
    updateNotificationsStatus: thunk((actions, payload) => {
        return axios
            .post<UpdateNotificationsData, UpdateNotificationsData>(
                `${API.issue}/update_notification_status/chat`, payload
            )
            .then((data) => {
                actions.updatedNotificationsStatus({ ...data, issueId: payload.issueId, type: payload.type });
            });
    }),
    updatedNotificationsStatus: action((state, payload) => {
        state.notifications.map((element) => {
            const isSameType = payload.type !== undefined ? Number(element.messageType) === Number(payload.type) : true;
            if(Number(element.issueId) === Number(payload.issueId) && isSameType && element.messageStatus !== true) {
                element.messageStatus = true;
            }
            return element;
        })
        state.unreadNotificationCount = Math.max(
            0,
            state.unreadNotificationCount - (payload?.numberOfNotificationsReads || 0)
        );
    }),
    markAllAsRead: thunk((actions, payload) => {
        const targetType = payload?.type;
        return axios
            .post(`${API.users}/mark_all_read/chat`, {
                ...(targetType !== undefined ? { chatType: targetType } : {})
            })
            .then(() => {
                actions.markAllAsReadSuccess(payload);
            });
    }),
    markAllAsReadSuccess: action((state, payload) => {
        const targetType = payload?.type;
        if (targetType !== undefined) {
            state.notifications.forEach((element) => {
                if (Number(element.messageType) === Number(targetType)) {
                    element.messageStatus = true;
                }
            });
            if (state.perTypeCount) {
                state.perTypeCount[targetType] = 0;
            }
            state.unreadNotificationCount = Object.values(state.perTypeCount || {}).reduce(
                (sum, cnt) => sum + (cnt || 0),
                0
            );
        } else {
            state.notifications.forEach((element) => {
                element.messageStatus = true;
            });
            state.unreadNotificationCount = 0;
            state.perTypeCount = {};
        }
    })
};

